// Frontend API service extending admin patterns with offline capabilities
import i18next from 'i18next';
import { getFullLocale } from './i18n';
import { PUBLIC_ACCOUNT_FEATURES_ENABLED } from '../config/publicFeatures';
import type { 
  APIResponse, 
  SpeciesData, 
  TechniqueData, 
  RigData,
  LocationTypeData, 
  CatchEntry,
  UserProfile,
  // REMOVED: RegulationData - legal liability eliminated
  SpeciesListParams,
  TechniquesListParams,
  RigsListParams,
  LocationTypesListParams,
  SetupGuideData,
  CreateCatchRequest,
  UpdateCatchRequest,
  UserProfileRequest,
  SupportedLocale 
} from '../types';
import { withSpeciesTargetability } from '../utils/speciesTargetability';
import { withNormalizedTechniqueCategory } from '../utils/techniqueTaxonomy';

// Use proxy during development, direct URL in production
const API_BASE_URL = import.meta.env.VITE_API_GATEWAY_URL || '/api';
const DEBUG_API = import.meta.env.DEV && import.meta.env.VITE_API_DEBUG === 'true';
const apiDebugLog = (...args: unknown[]) => {
  if (DEBUG_API) {
    console.log(...args);
  }
};

const tApi = (key: string, options?: Record<string, unknown>) =>
  String(i18next.t(`toolbox:services.api.${key}`, options));

apiDebugLog('🔗 API_BASE_URL configured as:', API_BASE_URL);

// Cache TTL configurations for different data types
export const CACHE_TTL = {
  SPECIES: 7 * 24 * 60 * 60 * 1000,      // 7 days - reference data
  TECHNIQUES: 7 * 24 * 60 * 60 * 1000,   // 7 days - reference data
  RIGS: 7 * 24 * 60 * 60 * 1000,        // 7 days - reference data
  LOCATIONS: 24 * 60 * 60 * 1000,        // 24 hours - may have seasonal updates
  WEATHER: 3 * 60 * 60 * 1000,           // 3 hours - frequently changing
  TIDES: 24 * 60 * 60 * 1000,           // 24 hours - predictable
  // REMOVED: REGULATIONS cache TTL - legal liability eliminated
} as const;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Offline cache entry structure
interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  expiresAt: number;
  etag?: string;
}

class FishingApiService {
  private cache = new Map<string, CacheEntry>();
  private isOnline = navigator.onLine;
  
  constructor() {
    // Monitor online/offline status
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.processOfflineQueue();
    });
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const baseHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept-Language': this.getCurrentLocale(),
    };

    if (!PUBLIC_ACCOUNT_FEATURES_ENABLED) {
      return baseHeaders;
    }

    try {
      const moduleName = 'aws-amplify/auth';
      const { fetchAuthSession } = await import(/* @vite-ignore */ moduleName);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        return baseHeaders;
      }

      return {
        ...baseHeaders,
        Authorization: `Bearer ${token}`
      };
    } catch {
      // Public routes should keep working when no authenticated session exists.
      return baseHeaders;
    }
  }

  private getCurrentLocale(): SupportedLocale {
    const storedLocale =
      localStorage.getItem('preferredLanguage') ||
      localStorage.getItem('selectedLocale');

    if (storedLocale && storedLocale.includes('-')) {
      return storedLocale as SupportedLocale;
    }

    const detectedLanguage = storedLocale || localStorage.getItem('i18nextLng') || 'en';
    return getFullLocale(detectedLanguage);
  }

  private getCacheKey(endpoint: string, params?: Record<string, any>): string {
    const paramStr = params ? `?${new URLSearchParams(params).toString()}` : '';
    return `${endpoint}${paramStr}`;
  }

  private toStringArray(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item).trim())
        .filter(Boolean);
    }

    if (typeof value === 'string') {
      return value
        .split(/\r?\n|,/)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  }

  private normalizeSpeciesData(species: SpeciesData): SpeciesData {
    return withSpeciesTargetability(species);
  }

  private normalizeAssetUrl(url: unknown, fallback = ''): string {
    const value = typeof url === 'string' ? url.trim() : '';
    return value && !/example\.com/i.test(value) ? value : fallback;
  }

  private normalizeTechniqueData(technique: TechniqueData): TechniqueData {
    const normalizedTechnique = withNormalizedTechniqueCategory(technique);
    const images = normalizedTechnique.images || { primary: '', diagrams: [], steps: [] };

    return {
      ...normalizedTechnique,
      images: {
        primary: this.normalizeAssetUrl(images.primary, '/images/technique-placeholder.webp'),
        diagrams: this.toStringArray(images.diagrams).map((url) => this.normalizeAssetUrl(url)).filter(Boolean),
        steps: this.toStringArray(images.steps).map((url) => this.normalizeAssetUrl(url)).filter(Boolean)
      }
    };
  }

  private normalizeRigData(rig: any): RigData {
    const components = rig?.components || {};
    const construction = rig?.construction || {};
    const applications = rig?.applications || {};
    const images = rig?.images || {};

    return {
      ...rig,
      targetSpeciesIds: this.toStringArray(rig?.targetSpeciesIds),
      suitableTechniqueIds: this.toStringArray(rig?.suitableTechniqueIds),
      suitableLocationTypes: this.toStringArray(rig?.suitableLocationTypes),
      components: {
        hooks: Array.isArray(components.hooks) ? components.hooks : [],
        weights: Array.isArray(components.weights) ? components.weights : [],
        lines: Array.isArray(components.lines) ? components.lines : [],
        accessories: Array.isArray(components.accessories) ? components.accessories : []
      },
      construction: {
        steps: this.toStringArray(construction.steps),
        knots: this.toStringArray(construction.knots),
        tips: this.toStringArray(construction.tips)
      },
      applications: {
        bestConditions: this.toStringArray(applications.bestConditions ?? applications.best_conditions),
        targetDepth: String(applications.targetDepth ?? applications.water_depth ?? ''),
        season: this.toStringArray(applications.season),
        baitTypes: this.toStringArray(applications.baitTypes ?? applications.bait_types)
      },
      advantages: this.toStringArray(rig?.advantages),
      disadvantages: this.toStringArray(rig?.disadvantages),
      variations: Array.isArray(rig?.variations)
        ? rig.variations.map((variation: any) => ({
            name: String(variation?.name ?? ''),
            description: String(variation?.description ?? ''),
            modifications: this.toStringArray(variation?.modifications)
          }))
        : [],
      images: {
        primary: String(images.primary ?? ''),
        diagrams: this.toStringArray(images.diagrams),
        photos: this.toStringArray(images.photos)
      },
      maintenanceTips: this.toStringArray(rig?.maintenanceTips ?? rig?.maintenance_tips)
    };
  }

  private getCacheEntry<T>(key: string): CacheEntry<T> | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    // Check if expired
    if (Date.now() > entry.expiresAt) {
      this.cache.delete(key);
      return null;
    }
    
    return entry as CacheEntry<T>;
  }

  private setCacheEntry<T>(key: string, data: T, ttl: number): void {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + ttl,
    };
    this.cache.set(key, entry);
  }


  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    cacheTTL?: number
  ): Promise<T> {
    const cacheKey = this.getCacheKey(endpoint, options.method === 'GET' ? {} : undefined);
    
    // Check cache first for GET requests
    if (options.method === 'GET' || !options.method) {
      const cached = this.getCacheEntry<T>(cacheKey);
      if (cached) {
        return cached.data;
      }
    }

    // If offline and no cache, throw error
    if (!this.isOnline && (!options.method || options.method === 'GET')) {
      throw new ApiError(tApi('offlineNoCache'), 503);
    }

    const headers = await this.getAuthHeaders();
    const baseUrl = API_BASE_URL?.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

    try {
      apiDebugLog(`🌐 Making fetch request to: ${baseUrl}${cleanEndpoint}`);
      
      // Add 5 second timeout to prevent hanging requests  
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.error(`⏰ Request timeout after 12s: ${baseUrl}${cleanEndpoint}`);
        controller.abort();
      }, 12000);
      
      const response = await fetch(`${baseUrl}${cleanEndpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          ...headers,
          ...options.headers
        }
      });
      clearTimeout(timeoutId);
      apiDebugLog(`📡 Fetch response received: ${response.status} ${response.statusText}`);
      apiDebugLog(`📡 Response headers:`, Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`;
        let errorCode = undefined;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          errorCode = errorData.code;
        } catch {
          // If JSON parsing fails, use default message
        }
        
        throw new ApiError(errorMessage, response.status, errorCode);
      }

      apiDebugLog(`📡 Parsing JSON response...`);
      const apiResponse: APIResponse<T> = await response.json();
      apiDebugLog(`📡 Parsed JSON:`, apiResponse);
      
      if (!apiResponse.success) {
        console.error('❌ API response not successful:', apiResponse);
        throw new ApiError(apiResponse.error || tApi('requestFailed'), 400);
      }

      const data = apiResponse.data!;
      apiDebugLog(`✅ Successfully extracted data:`, data);
      
      // Cache successful GET responses
      if ((!options.method || options.method === 'GET') && cacheTTL) {
        this.setCacheEntry(cacheKey, data, cacheTTL);
      }

      return data;
    } catch (error) {
      // If offline and we have cache, return stale data
      if (!this.isOnline) {
        const cached = this.getCacheEntry<T>(cacheKey);
        if (cached) {
          console.warn('Using stale cache due to network error');
          return cached.data;
        }
      }
      throw error;
    }
  }

  // Public content APIs (no auth required, heavily cached)
  async getSpeciesList(params: SpeciesListParams): Promise<SpeciesData[]> {
    // Filter out undefined values to avoid sending "undefined" strings
    const cleanParams: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            cleanParams[key] = value.join(',');
          }
        } else {
          cleanParams[key] = String(value);
        }
      }
    });
    
    
    const queryParams = new URLSearchParams(cleanParams).toString();
    const response = await this.makeRequest<{ items: SpeciesData[]; pagination: any }>(
      `/species?${queryParams}`,
      { method: 'GET' },
      CACHE_TTL.SPECIES
    );
    
    // Extract just the items array from pagination response
    return response.items.map((species) => this.normalizeSpeciesData(species));
  }

  async getSpeciesDetail(speciesId: string, locale: SupportedLocale): Promise<SpeciesData> {
    const species = await this.makeRequest<SpeciesData>(
      `/species/${speciesId}?locale=${locale}`,
      { method: 'GET' },
      CACHE_TTL.SPECIES
    );

    return this.normalizeSpeciesData(species);
  }

  async getTechniquesList(params: TechniquesListParams): Promise<TechniqueData[]> {
    // Filter out undefined values to avoid sending "undefined" strings
    const cleanParams: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            cleanParams[key] = value.join(',');
          }
        } else {
          cleanParams[key] = String(value);
        }
      }
    });
    
    const queryParams = new URLSearchParams(cleanParams).toString();
    const response = await this.makeRequest<{ items: TechniqueData[]; pagination: any }>(
      `/techniques?${queryParams}`,
      { method: 'GET' },
      CACHE_TTL.TECHNIQUES
    );
    
    // Extract just the items array from pagination response
    return response.items.map((technique) => this.normalizeTechniqueData(technique));
  }

  async getTechniqueDetail(techniqueId: string, locale: SupportedLocale): Promise<TechniqueData> {
    const technique = await this.makeRequest<TechniqueData>(
      `/techniques/${techniqueId}?locale=${locale}`,
      { method: 'GET' },
      CACHE_TTL.TECHNIQUES
    );

    return this.normalizeTechniqueData(technique);
  }

  async getRigsList(params: RigsListParams): Promise<RigData[]> {
    // Filter out undefined values to avoid sending "undefined" strings
    const cleanParams: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            cleanParams[key] = value.join(',');
          }
        } else {
          cleanParams[key] = String(value);
        }
      }
    });
    
    const queryParams = new URLSearchParams(cleanParams).toString();
    const response = await this.makeRequest<{ items: RigData[]; pagination: any }>(
      `/rigs?${queryParams}`,
      { method: 'GET' },
      CACHE_TTL.RIGS
    );
    
    return (response.items || []).map((rig) => this.normalizeRigData(rig));
  }

  async getRigDetail(rigId: string, locale: SupportedLocale): Promise<RigData> {
    const rig = await this.makeRequest<RigData>(
      `/rigs/${rigId}?locale=${locale}`,
      { method: 'GET' },
      CACHE_TTL.RIGS
    );

    return this.normalizeRigData(rig);
  }

  async getLocationTypesList(params: LocationTypesListParams): Promise<LocationTypeData[]> {
    // Filter out undefined values to avoid sending "undefined" strings
    const cleanParams: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            cleanParams[key] = value.join(',');
          }
        } else {
          cleanParams[key] = String(value);
        }
      }
    });
    
    const queryParams = new URLSearchParams(cleanParams).toString();
    const response = await this.makeRequest<{ items: LocationTypeData[]; pagination: any }>(
      `/locations?${queryParams}`,
      { method: 'GET' },
      CACHE_TTL.LOCATIONS
    );
    
    // Extract just the items array from pagination response
    return response.items;
  }

  async getSetupGuidesList(params: { locale: SupportedLocale; slot?: string; search?: string; limit?: number }): Promise<SetupGuideData[]> {
    const cleanParams: Record<string, string> = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        cleanParams[key] = String(value);
      }
    });

    const queryParams = new URLSearchParams(cleanParams).toString();
    const response = await this.makeRequest<{ items: SetupGuideData[]; pagination: any }>(
      `/recommendations/catalog?entityType=setup_guides&${queryParams}`,
      { method: 'GET' },
      CACHE_TTL.RIGS
    );

    return response.items || [];
  }

  async getSetupGuideDetail(setupGuideId: string, locale: SupportedLocale): Promise<SetupGuideData> {
    return this.makeRequest<SetupGuideData>(
      `/recommendations/catalog?entityType=setup_guides&id=${setupGuideId}&locale=${locale}`,
      { method: 'GET' },
      CACHE_TTL.RIGS
    );
  }

  async getLocationTypeDetail(locationTypeId: string, locale: SupportedLocale): Promise<LocationTypeData> {
    apiDebugLog(`Getting location type detail for: ${locationTypeId}`);

    try {
      const locationType = await this.makeRequest<LocationTypeData>(
        `/locations/${locationTypeId}?locale=${locale}`,
        { method: 'GET' },
        CACHE_TTL.LOCATIONS
      );

      apiDebugLog(`Found location type via detail endpoint: ${locationType.name}`);
      return locationType;
    } catch (error) {
      apiDebugLog(`Location type detail endpoint failed for ${locationTypeId}; falling back to list lookup`, error);

      const locationTypesList = await this.getLocationTypesList({ locale, limit: 100 });
      const locationTypeFromList = locationTypesList.find((lt) => lt.locationTypeId === locationTypeId);

      if (locationTypeFromList) {
        apiDebugLog(`Found location type via list fallback: ${locationTypeFromList.name}`);
        return locationTypeFromList;
      }

      throw new ApiError(`Location type not found: ${locationTypeId}`, 404);
    }
  }

  // REMOVED: getRegulations method - legal liability eliminated

  // User-specific APIs (auth required)
  async getUserProfile(): Promise<UserProfile> {
    return this.makeRequest<UserProfile>('/profile', { method: 'GET' });
  }

  async updateUserProfile(data: UserProfileRequest): Promise<UserProfile> {
    return this.makeRequest<UserProfile>('/profile', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async getCatchesList(params?: any): Promise<CatchEntry[]> {
    const queryParams = params ? `?${new URLSearchParams(params).toString()}` : '';
    const response = await this.makeRequest<{ items: CatchEntry[]; pagination: any }>(`/catches${queryParams}`, { method: 'GET' });
    
    // Extract just the items array from pagination response
    return response.items;
  }

  async createCatch(data: CreateCatchRequest): Promise<CatchEntry> {
    return this.makeRequest<CatchEntry>('/catches', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateCatch(catchId: string, data: UpdateCatchRequest): Promise<CatchEntry> {
    return this.makeRequest<CatchEntry>(`/catches/${catchId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteCatch(catchId: string): Promise<void> {
    return this.makeRequest<void>(`/catches/${catchId}`, { method: 'DELETE' });
  }

  // External integrations - Use specific weather/tides services instead
  // Deprecated: Use weatherService.ts and tidesService.ts for environmental data

  // Offline queue processing (placeholder - will be implemented with Zustand store)
  private async processOfflineQueue(): Promise<void> {
    apiDebugLog(tApi('processingOfflineQueue'));
    // This will be implemented in the offline store
  }

  // Cache management
  clearCache(): void {
    this.cache.clear();
  }

  getCacheStats(): { size: number; entries: string[] } {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys())
    };
  }

  // Generic POST method for weather and other endpoints
  async post<T = any>(endpoint: string, data: any): Promise<{ data: T }> {
    const response = await this.makeRequest<T>(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data)
    });
    return { data: response };
  }

  // Generic GET method for authenticated endpoints with query parameters
  async get<T = any>(endpoint: string, params?: Record<string, any>): Promise<{ data: T }> {
    const queryString = params ? `?${new URLSearchParams(
      Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(([key, value]) => [key, String(value)])
    ).toString()}` : '';
    
    const response = await this.makeRequest<T>(`${endpoint}${queryString}`, {
      method: 'GET'
    });
    return { data: response };
  }
}

// Export singleton instance
export const fishingApiService = new FishingApiService();

export { FishingApiService };
export default FishingApiService;
